import { type Segment } from '../geometry';
import { type Vec2, vec } from '../math';

import { buildMesh, type Edge, type Mesh, SolidKind } from './mesh';
import { occludeSegment, type Piece, viewerDirection } from './occlude';
import { type RoundBody } from './plane';
import { createProjector, ProjectionKind, type Projector } from './project';
import { NO_ROTATION, vec3, type Vec3 } from './Vec3';
import {
  edgeKey,
  type Lattice,
  projectWith,
  rimConics,
  type SolidCurve,
  type SolidFrame,
  sphereConics,
  sphereCircle,
  sphereSectionBasis,
} from './wireframe';

/**
 * Solids whose corners have names.
 *
 * ## Why the built-in solid was not enough
 *
 * The editor has had cubes, prisms and pyramids since M9, and every problem in the exercise
 * book opens by naming their vertices — *"Cho hình chóp S.ABCD…"*, *"Gọi I là trung điểm của
 * CC'"*. A wireframe with no named corners cannot answer either sentence: there is nothing to
 * label, nothing to take a midpoint of, nothing to draw a plane through. So the handsome box
 * the tool produced was, for the purpose the product exists for, decoration.
 *
 * What follows builds the same solid and hands back *points* — real entities the rest of the
 * language can name, hide, style and construct on. The hidden-line work is not redone: the
 * mesh goes through the same exact classifier the painted solid uses, and only the reporting
 * differs.
 *
 * ## Why the mesh is built here rather than taken from `mesh.ts`
 *
 * Because the caller chose the order of the names. `buildMesh` numbers a box front-face-first,
 * which is its own business and none of the script's; a teacher writing `box A B C D . A' B' C'
 * D'` means the bottom face in that order and the top face above it. Building the rings in the
 * order they were written is what makes the two agree, and it costs a dozen lines.
 */

/** How the solid sits on the page. */
export interface SolidPlacement {
  /** Where the centre of the solid lands. */
  readonly at: Vec2;
  /** Extent across the page. */
  readonly width: number;
  /** Extent upright. */
  readonly height: number;
  /** Extent into the page. */
  readonly depth: number;
}

export interface NamedVertex {
  readonly name: string;
  readonly at: Vec2;
  /**
   * Where the corner is in space, before projection.
   *
   * Carried out because a solid is **opaque**, and only the three-dimensional position knows
   * that. Two corners that project either side of the figure may be joined by a line running
   * straight through the middle of the block, and on the page there is nothing left to tell
   * you so — the segment looks exactly like one drawn across the front face.
   */
  readonly in3d: Vec3;
}

export interface NamedEdge {
  readonly a: string;
  readonly b: string;
  /** Behind the solid: drawn dashed, which is the convention and also the whole point. */
  readonly hidden: boolean;
}

/**
 * A run of the drawing that no named corner sits on.
 *
 * A cone and a cylinder have no corners to speak of — an apex, a centre or two, and a rim that
 * is a curve rather than a chain of edges. Their outline is therefore *drawn* rather than
 * *named*: two silhouette generators and the visible and hidden halves of each rim, each one a
 * run of points that is styled as a whole.
 *
 * The halves are not a nicety. A cone standing on its base hides the far half of its own rim
 * behind its body, and a figure that draws the whole ellipse solid says the base is a disc seen
 * from nowhere in particular. Every textbook dashes the back.
 */
export interface OutlineRun {
  readonly points: readonly Vec2[];
  readonly hidden: boolean;
  /**
   * An arc in {@link NamedSolid.arcs} already draws this run, which is kept only to be measured.
   *
   * A rim is a circle, and a parallel projection carries a circle to an ellipse exactly, so the
   * drawn rim is a real conic and stays round at any magnification. The chords remain in the
   * value because bounds, hit-testing and the spatial index all measure rather than draw — but
   * stroking both would lay a polyline over its own ellipse, which thickens on export and shows
   * as a shadow beside the curve wherever a reader zooms in far enough to care.
   */
  readonly covered?: boolean;
}

/** A base circle, after projection. */
export interface NamedCircle {
  readonly name: string;
  readonly center: Vec2;
  readonly rx: number;
  readonly ry: number;
  readonly rotation: number;
}

export interface NamedSolid {
  readonly vertices: readonly NamedVertex[];
  readonly edges: readonly NamedEdge[];
  /** Drawn but unnamed geometry: the curved parts. Empty for a prism or a pyramid. */
  readonly outline?: readonly OutlineRun[];
  /**
   * The base circles, as the ellipses they project to.
   *
   * Not drawn — the visible rim is the two halves in {@link outline}, which is the only way to
   * dash the far one. These exist so the *language* has something to attach to: `(O)` is an
   * object a problem talks about, and without it there is no way to write "the point where this
   * line meets the base circle", nor for the corpus to check that a rim point really is on the
   * rim rather than merely near it.
   */
  readonly circles?: readonly NamedCircle[];
  /**
   * The round parts as real conics: rims, and a sphere's outline and equator.
   *
   * Every run of {@link outline} they cover is marked so, and what is left is the genuinely
   * straight part — a cone's two silhouette generators, which are lines and would be wrong as
   * anything else.
   */
  readonly arcs?: readonly SolidCurve[];
  /**
   * The body itself, for the constructions that work in space rather than on the page.
   *
   * A **plane** needs all three: the corners to cut through, the faces to clip a line against,
   * and the projection to put the answer back on the page. Present only for the solids whose
   * faces are flat — a cone's lateral surface is not a face a section can be clipped to, and a
   * plane through one would need a conic rather than a polygon.
   */
  readonly body?: {
    readonly vertices: readonly Vec3[];
    readonly faces: readonly (readonly number[])[];
    readonly edges: readonly { readonly a: number; readonly b: number }[];
  };
  /**
   * The radii and taper, for a round one.
   *
   * The counterpart of `body` for the solids that have no flat sides to clip against. A plane
   * square to a cylinder's axis cuts a *circle*, and cutting the tessellation instead would give
   * a section of a sixty-four-sided prism drawn as a chain of chords.
   */
  readonly round?: RoundBody;
  /**
   * A point on a base circle, `degrees` round from the front.
   *
   * Present only on the round solids, because only they have a rim. The angle is measured in
   * the *base plane*, not on the page — the author is describing the solid, and "hai đường
   * sinh SA và SB" is a statement about where A and B sit on the circle, not about where the
   * projection happens to put them.
   */
  readonly rimAt?: (centre: string, degrees: number) => NamedVertex | null;
  /**
   * Where the solid covers a segment between two points in space, as stretches along it.
   *
   * ## Why the figure needs this
   *
   * Because a block is not made of glass. The lines a *problem* adds are as liable to run
   * behind it as the solid's own edges are: the diagonal `A'C` of a cube goes corner to
   * opposite corner straight through the middle, and the common perpendicular between two
   * skew edges lives entirely inside. Drawn solid they read as lying on the near face, which
   * is a claim about the figure that is simply false — and the reader who believes it will
   * mis-picture the whole problem.
   *
   * Dashing the solid's own hidden edges and leaving these alone is the half-measure that
   * shipped first. It looks finished, because the *shape* is right; what is wrong is
   * everything drawn on top of it.
   *
   * ## Why stretches rather than a flag
   *
   * This answered `boolean`, decided by the segment's **midpoint**: inside the body means
   * dashed. That is exact for a segment wholly in or wholly out and silently wrong for one
   * running half behind — it was drawn entirely one way, and which way depended on where its
   * middle happened to fall. `AM` from a corner to a point on the far face flipped between solid
   * and dashed as `M` was dragged past halfway, with nothing about the figure changing.
   *
   * The midpoint rule also assumed a **convex** body, which every built-in one is and a body a
   * teacher builds need not be. Cutting answers both, and it is the same cutting the solid's own
   * edges get — one rule for "what does this body hide" rather than two that can disagree.
   */
  readonly occlude: (a: Vec3, b: Vec3) => readonly Piece[];
  /**
   * Where a point in space lands on the page.
   *
   * Every solid carries its own, because a construction that has to be done in space — the
   * foot of a perpendicular, a point on a rim — produces a position that still has to be drawn,
   * and only the solid knows the projection and the origin it was placed at.
   */
  readonly project: (at: Vec3) => Vec2;
}

/**
 * The oblique view every figure in the book is drawn in.
 *
 * The front face is true — a square base really is drawn as a parallelogram with a horizontal
 * front edge — and the depth axis runs away from the reader, up and to the right. So the
 * visible faces are the front, the top and the right, which is the picture a teacher draws by
 * hand and the one the printed figures use.
 *
 * That is exactly cabinet projection, and for a while this file carried its own copy of it,
 * because the shipped one drew a box as four solid lines and eight dashes. That was a bug in
 * the projector rather than a difference of opinion — see `isFrontFacing` in `project.ts` —
 * and once it was fixed the copy had nothing left to say.
 *
 * The swap was checked by comparing the exported SVGs before and after: every stroke in the
 * four corpus figures comes out at the same two endpoints in the same colour, so the same
 * edges are dashed and the same lines drawn. Only the *order* of the elements changed, which
 * moves a pixel or two where two strokes cross and nothing else.
 *
 * Fixed rather than configurable, for now. A turn knob is easy to add and hard to remove, and
 * nothing in two hundred problems has yet needed one.
 */
const VIEW: Projector = createProjector(ProjectionKind.Cabinet, NO_ROTATION);

/**
 * A prism: two congruent rings, the second directly above the first.
 *
 * Covers `box` — a prism on four corners — as the same construction with a different count,
 * which is why there is no separate box builder. `bottom` and `top` must be the same length
 * and are paired by position: the *k*th name of one sits under the *k*th name of the other.
 */
export function namedPrism(
  bottom: readonly string[],
  top: readonly string[],
  placement: SolidPlacement,
  foot: readonly number[] = [],
  corner?: BaseCorner,
  explicit?: readonly { x: number; z: number }[]
): NamedSolid {
  const n = bottom.length;
  const ring = baseRing(n, placement, corner, explicit);
  const half = placement.height / 2;

  /*
   * The lean, which is what makes a prism oblique.
   *
   * `foot` names the base corners the *first* top corner stands over — none or that corner
   * itself for an upright prism, one other for a slanted one, two for the midpoint of the edge
   * between them. It reads exactly as the pyramid's `foot` does, so one sentence covers both:
   * the thing that goes up is directly above the average of these.
   *
   * "Lăng trụ ABC.A'B'C' có hình chiếu vuông góc của A' lên (ABC) trùng với trung điểm H của BC"
   * is the problem this exists for, and the language had nothing for it: `prism` translated the
   * base straight up and nothing else. A model asked for that figure placed six points by hand,
   * which loses every position in space — so no edge could be dashed, and an angle mark it added
   * measured 130° on the page under a label reading 60°.
   *
   * The whole top ring shifts by the same vector, because a prism is a translation. Only where
   * that vector points changes; a prism whose top face is not congruent to its bottom is not a
   * prism at all.
   */
  const over = foot.flatMap((index) => {
    const point = ring[index];
    return point === undefined ? [] : [point];
  });
  const first = ring[0] ?? { x: 0, z: 0 };
  const lean =
    over.length === 0
      ? { x: 0, z: 0 }
      : {
          x: over.reduce((sum, point) => sum + point.x, 0) / over.length - first.x,
          z: over.reduce((sum, point) => sum + point.z, 0) / over.length - first.z,
        };

  const vertices: Vec3[] = [
    ...ring.map((point) => vec3(point.x, -half, point.z)),
    ...ring.map((point) => vec3(point.x + lean.x, half, point.z + lean.z)),
  ];

  /*
   * Winding: a face is listed counter-clockwise *seen from outside*. The bottom ring runs
   * anticlockwise viewed from above, so the top face keeps that order and the bottom face
   * reverses it. A side face walks along the bottom edge and back along the top.
   *
   * Getting one of these backwards inverts that face's normal and the classifier then calls
   * the wrong edges hidden — a figure that is subtly, confidently wrong, and one that no
   * amount of reasoning about handedness settles reliably. `solids.test.ts` asserts which
   * corner the dashes meet at, which is the thing a reader would notice.
   */
  const faces: number[][] = [
    [...Array.from({ length: n }, (_, k) => n - 1 - k)],
    [...Array.from({ length: n }, (_, k) => n + k)],
    ...Array.from({ length: n }, (_, k) => {
      const next = (k + 1) % n;
      return [k, next, n + next, n + k];
    }),
  ];

  return named(vertices, faces, [...bottom, ...top], placement);
}

/**
 * A pyramid: a ring, and one apex above a point of the base plane.
 *
 * ## Why the apex is not always over the centre
 *
 * Because most of the problems do not put it there. `SA ⊥ (ABCD)` — the apex standing directly
 * over a corner — is among the commonest phrasings in the book, and `(SAB) ⊥ (ABCD)` with `SAB`
 * equilateral puts it over the midpoint of an edge. A tool that can only draw the regular case
 * draws the picture for neither, and what it draws instead is not a simplification but a
 * *different solid*: `SA` is perpendicular to the base in one and slanted in the other, and that
 * is the whole content of the question.
 *
 * `foot` names the base-ring corners the apex stands over — none for the centre, one for a
 * corner, two for the midpoint of the edge between them. It is the *chân đường cao*, and putting
 * it on a corner is what makes the lateral edge project as a true vertical, which is exactly how
 * a reader recognises the perpendicularity the problem states.
 */
export function namedPyramid(
  apex: string,
  base: readonly string[],
  placement: SolidPlacement,
  foot: readonly number[] = [],
  corner?: BaseCorner,
  explicit?: readonly { x: number; z: number }[]
): NamedSolid {
  const n = base.length;
  const ring = baseRing(n, placement, corner, explicit);
  const half = placement.height / 2;

  const over = foot.flatMap((index) => {
    const point = ring[index];
    return point === undefined ? [] : [point];
  });
  const above =
    over.length === 0
      ? { x: 0, z: 0 }
      : {
          x: over.reduce((sum, point) => sum + point.x, 0) / over.length,
          z: over.reduce((sum, point) => sum + point.z, 0) / over.length,
        };

  const vertices: Vec3[] = [
    ...ring.map((point) => vec3(point.x, -half, point.z)),
    vec3(above.x, half, above.z),
  ];

  const faces: number[][] = [
    [...Array.from({ length: n }, (_, k) => n - 1 - k)],
    ...Array.from({ length: n }, (_, k) => [k, (k + 1) % n, n]),
  ];

  return named(vertices, faces, [...base, apex], placement);
}

/**
 * The base polygon, in the x–z plane, running anticlockwise seen from above.
 *
 * Four corners give a **rectangle** `width × depth` rather than a square inscribed in it,
 * because four corners nearly always means a square, a rectangle or a rhombus base and the
 * reader is meant to see a floor. Other counts give a regular polygon inscribed in the same
 * ellipse, which is what a hexagonal prism should look like and is at least not surprising.
 *
 * The first corner is at the *front* — nearest the reader, which under cabinet is `+z` — so
 * `A` is the corner closest to the front and the figure reads the way the names do.
 */
/**
 * A base read back off a shape drawn on the page.
 *
 * ## Why the drawing is taken as the *answer* rather than as a plan
 *
 * A teacher who draws the floor of a solid has already arranged it to look right — foreshortened,
 * leaning the way the figure leans. Two readings were possible and only one of them respects
 * that: treat the outline as a plan seen from above and the shape they drew is not the shape they
 * get, because the projection shears it on the way in. So the outline is read as the base's
 * **image**, and the solid is built so that its base projects exactly back onto it.
 *
 * That is an inversion, and it exists because the projection of the base plane is invertible: a
 * point `(x, 0, z)` lands at `x·ax + z·az`, two unknowns and two equations. Cabinet's depth axis
 * is not parallel to its width axis, so the determinant is never zero — a fact about this view
 * rather than an assumption, and the reason `null` is returned instead if it ever is.
 *
 * The height is not recoverable and is not asked for: a floor drawn on paper says nothing about
 * how tall the body standing on it is, and the caller chooses.
 */
export function baseFromPage(points: readonly Vec2[]): readonly number[] | null {
  const frame = frameOfView(vec.vec2(0, 0));
  const determinant = frame.x.x * frame.z.y - frame.x.y * frame.z.x;
  if (Math.abs(determinant) < 1e-12) return null;

  return points.flatMap((point) => [
    (point.x * frame.z.y - point.y * frame.z.x) / determinant,
    (frame.x.x * point.y - frame.x.y * point.x) / determinant,
  ]);
}

/**
 * A base given corner by corner, checked and turned the right way round.
 *
 * The escape hatch for the base no clause describes — an irregular quadrilateral, a five-sided
 * plot, whatever a particular problem happens to state. Everything else about the solid still
 * works from it: the corners get names, the edges get hidden-line treatment, and constructions
 * hang off them exactly as before.
 *
 * Three things are settled here rather than left to the writer, because getting any of them
 * wrong produces a figure that is wrong without looking wrong.
 *
 * **Winding.** A face is listed anticlockwise seen from outside, and which way the corners were
 * typed decides which way every normal points. Typed the other way round, the classifier calls
 * the complement of the right edges hidden — a solid drawn inside out. So the list is reversed
 * when needed rather than refused: both orders name the same polygon and a reader has no reason
 * to know the convention.
 *
 * **Convexity.** The occlusion test is exact *because* the solid is convex: the midpoint of a
 * segment lies inside iff the whole segment does. A dented base breaks that silently — some
 * lines through the dent come out solid and some dashed, with no pattern to notice. Refused.
 *
 * **Centring.** `at` places the centre of the solid, so the ring is moved onto its own bounding
 * box like every other base. Without it a polygon typed around some other origin hangs off to
 * one side of the axis its top ring stands on.
 */
export function orientedBase(
  flat: readonly number[],
  corners: number
): readonly { x: number; z: number }[] | string {
  if (flat.length !== corners * 2) {
    return `a base of ${String(corners)} corners needs ${String(corners * 2)} numbers, not ${String(flat.length)}`;
  }
  if (flat.some((value) => !Number.isFinite(value))) return 'a base takes plain numbers';

  const ring = Array.from({ length: corners }, (_, k) => ({
    x: flat[k * 2] ?? 0,
    z: flat[k * 2 + 1] ?? 0,
  }));

  const turn = (k: number): number => {
    const a = ring[k] ?? { x: 0, z: 0 };
    const b = ring[(k + 1) % corners] ?? { x: 0, z: 0 };
    const c = ring[(k + 2) % corners] ?? { x: 0, z: 0 };
    return (b.x - a.x) * (c.z - b.z) - (b.z - a.z) * (c.x - b.x);
  };

  /*
   * Every corner has to turn *somewhere*, which is not the same as every corner turning the same
   * way. Three corners in a line put a vertex in the middle of an edge: harmless to look at, and
   * it gives the mesh a face with a zero-length crease and the namer an edge nobody can point at.
   * A dent is a shape; a spike is a mistake.
   */
  const turns = Array.from({ length: corners }, (_, k) => turn(k));
  if (turns.some((value) => Math.abs(value) < 1e-9)) {
    return 'a base needs corners that actually turn: three of these are in a straight line, or two coincide';
  }

  /*
   * The convention every other base uses is a negative turn, so a list typed the other way round
   * is walked backwards — but from the *same* first corner.
   *
   * Reversing the whole list would have been simpler and wrong: it moves A to where D was, so a
   * writer who happened to type their corners clockwise gets their labels shuffled while the
   * shape stays put. Reversing a cycle flips its orientation wherever you start it.
   *
   * Which way it winds is read from the **signed area** rather than from the turn at the first
   * corner. The two agree on every convex ring, and only one of them answers for a dented one:
   * there the turn at a corner says which corner it is, not which way the ring goes.
   *
   * Negative is the convention kept, matching the negative *turn* the old test looked for — a
   * clockwise ring in this frame. Written the other way round first, which reversed every base
   * that had been right for a year and showed up as one corpus figure measuring 240 across where
   * it had always measured 221.
   */
  const twiceArea = ring.reduce((sum, point, index) => {
    const next = ring[(index + 1) % corners] ?? point;
    return sum + (point.x * next.z - next.x * point.z);
  }, 0);

  const first = ring[0] ?? { x: 0, z: 0 };
  const ordered = twiceArea < 0 ? ring : [first, ...ring.slice(1).reverse()];

  const xs = ordered.map((point) => point.x);
  const zs = ordered.map((point) => point.z);
  const dx = (Math.min(...xs) + Math.max(...xs)) / 2;
  const dz = (Math.min(...zs) + Math.max(...zs)) / 2;
  return ordered.map((point) => ({ x: point.x - dx, z: point.z - dz }));
}

/**
 * How wide and how deep the base actually is, once its angle has been applied.
 *
 * Exported because the parser refuses a base so much longer than it is deep that it reads as a
 * line rather than a face, and it used to make that judgement from the two `size` numbers. With
 * an angle those are the *sides*, not the extents: a rhombus of side 210 with a 5° corner is a
 * sliver 419 long and 18 deep, and the old check saw 210 by 210 and waved it through.
 *
 * Measured from the ring the solid is really built on, so there is one shape and not two — a
 * second implementation of the same geometry in the parser is precisely how a check comes to
 * disagree with the thing it is checking.
 */
export function baseExtent(
  n: number,
  placement: SolidPlacement,
  corner?: BaseCorner,
  explicit?: readonly { x: number; z: number }[]
): { readonly width: number; readonly depth: number } {
  const ring = baseRing(n, placement, corner, explicit);
  const xs = ring.map((point) => point.x);
  const zs = ring.map((point) => point.z);
  return {
    width: Math.max(...xs) - Math.min(...xs),
    depth: Math.max(...zs) - Math.min(...zs),
  };
}

/** A base angle the problem stated: which corner it is at, and how many degrees. */
export interface BaseCorner {
  readonly at: number;
  readonly degrees: number;
  /**
   * The far parallel side, for a trapezium. Absent means it equals the near one, which closes
   * the shape into a parallelogram — so this is the one number that tells the two apart.
   */
  readonly far?: number;
  /**
   * How far along the parallels the far side starts, from the previous corner.
   *
   * Zero leaves it flush, which with a right angle is the "hình thang vuông tại A và D" of the
   * book. Half the difference between the two parallels centres it, and the two legs come out
   * mirror images of each other — "hình thang cân". One number covers both, and the shape is
   * still built by the one construction the parallelogram uses.
   */
  readonly offset?: number;
}

function baseRing(
  n: number,
  placement: SolidPlacement,
  corner?: BaseCorner,
  explicit?: readonly { x: number; z: number }[]
): readonly { x: number; z: number }[] {
  // A base given corner by corner answers the question outright; nothing below applies to it.
  if (explicit !== undefined) return explicit;

  const rx = placement.width / 2;
  const rz = placement.depth / 2;

  /*
   * A base with an angle the problem states — a right-angled triangle, a rhombus, a
   * parallelogram — which between them are most of the bases in the book.
   *
   * Without this a three-cornered base is regular and a four-cornered one is a rectangle, so
   * "đáy là tam giác vuông tại B" and "đáy ABCD là hình thoi, góc BAD = 60°" both came out as
   * some other shape. The figure then disagreed with its own caption, and an `anglemark` at the
   * corner truthfully reported the angle it found rather than the one the problem gave.
   *
   * The result is a rotation of one list, because rotating preserves the winding and the winding
   * decides which faces point outwards. Building the corners in a different order for each
   * `at` would invert a normal for most values of it and dash the complement of the right edges
   * — a figure confidently inside out.
   */
  if ((n === 3 || n === 4) && corner !== undefined) {
    /*
     * Which side is which is forced, not chosen.
     *
     * The names run round the ring, so the corner *before* the named one and the corner *after*
     * it are the two the sides go to: back is the width, on is the depth. One rule for both
     * shapes, and it has to be stated because a problem picks whichever two letters suit it —
     * "AB = a, BC = a√3" names the back side first, "AB = a, AD = 2a" names the forward one.
     *
     * Getting this backwards produces a figure with the right angle at the right corner and the
     * two sides swapped, which looks entirely plausible. The first version of this did.
     */
    const theta = (corner.degrees * Math.PI) / 180;
    const unit = { x: Math.cos(theta), z: Math.sin(theta) };
    const back = { x: placement.width, z: 0 };
    const on = { x: placement.depth * unit.x, z: placement.depth * unit.z };

    /*
     * A trapezium is the same figure with the far parallel side a different length.
     *
     * Walking A → B → C → D, the side out of the named corner and the side back into it from the
     * far corner are the two parallels; making them equal closes a parallelogram, and making them
     * differ opens a trapezium. So there is one construction rather than two, and `far` defaulting
     * to `depth` is exactly what "the parallelogram case" means.
     *
     * With the angle at 90 this is the "hình thang vuông tại A và D" of the book: the side between
     * the two parallels is perpendicular to both, so the right angles land at the named corner and
     * the one before it, which is where the problem says they are.
     */
    const reach = corner.far ?? placement.depth;
    const shift = corner.offset ?? 0;
    const start = { x: back.x + shift * unit.x, z: back.z + shift * unit.z };
    const far = { x: start.x + reach * unit.x, z: start.z + reach * unit.z };

    // Successor order from the named corner: itself, the one it leads to, then the rest.
    const raw = n === 3 ? [{ x: 0, z: 0 }, on, back] : [{ x: 0, z: 0 }, on, far, start];

    /*
     * Re-centred on its own bounding box, because `at` places the *centre* of the solid and a
     * sheared base built from one corner is not centred on anything. A rhombus left where it was
     * built hangs off to one side of the axis the top ring stands on.
     */
    const xs = raw.map((point) => point.x);
    const zs = raw.map((point) => point.z);
    const dx = (Math.min(...xs) + Math.max(...xs)) / 2;
    const dz = (Math.min(...zs) + Math.max(...zs)) / 2;

    const first = ((corner.at % n) + n) % n;
    return Array.from({ length: n }, (_, k) => {
      const point = raw[(k - first + n) % n] ?? { x: 0, z: 0 };
      return { x: point.x - dx, z: point.z - dz };
    });
  }

  if (n === 4) {
    return [
      { x: -rx, z: rz },
      { x: rx, z: rz },
      { x: rx, z: -rz },
      { x: -rx, z: -rz },
    ];
  }

  return Array.from({ length: n }, (_, k) => {
    const angle = Math.PI / 2 + (2 * Math.PI * k) / n;
    return { x: -rx * Math.cos(angle), z: rz * Math.sin(angle) };
  });
}

/** Projects the mesh and pairs every vertex and every edge back up with its name. */
function named(
  vertices: readonly Vec3[],
  faces: readonly (readonly number[])[],
  names: readonly string[],
  placement: SolidPlacement
): NamedSolid {
  const mesh: Mesh = { vertices, faces, edges: edgesOf(faces) };
  const lattice = projectWith(mesh, VIEW, placement.at);

  const at = (index: number): Vec2 => lattice.points[index] ?? vec.vec2(0, 0);
  const nameOf = (index: number): string => names[index] ?? '';
  const inSpace = (index: number): Vec3 => vertices[index] ?? vec3(0, 0, 0);

  return {
    vertices: names.map((name, index) => ({ name, at: at(index), in3d: inSpace(index) })),
    edges: lattice.edges.map((edge) => ({
      a: nameOf(edge.a),
      b: nameOf(edge.b),
      hidden: edge.hidden,
    })),
    body: {
      vertices,
      faces,
      edges: mesh.edges.map((edge) => ({ a: edge.a, b: edge.b })),
    },
    occlude: occlusionPieces(mesh),
    project: (point) => onPage(point, placement.at),
  };
}

/**
 * The one projection every named solid is drawn with, as four points.
 *
 * Named solids are always cabinet and never rotated — the figure in a paper is drawn one way —
 * so this is the same map every time and only the origin moves. Four points settle it because a
 * parallel projection is affine, and carrying data rather than a function keeps a value a value.
 */
function frameOfView(origin: Vec2): SolidFrame {
  const axis = (x: number, y: number, z: number): Vec2 => {
    const image = VIEW.toPlane(VIEW.toView(vec3(x, y, z)));
    return { x: image.x, y: image.y };
  };

  return { origin, x: axis(1, 0, 0), y: axis(0, 1, 0), z: axis(0, 0, 1) };
}

/** A point in space, on the page. Shared by every solid; only the origin differs. */
function onPage(point: Vec3, origin: Vec2): Vec2 {
  const planar = VIEW.toPlane(VIEW.toView(point));
  return vec.vec2(planar.x + origin.x, planar.y + origin.y);
}

/**
 * Where a solid covers a segment, as stretches along it.
 *
 * ## What this replaces
 *
 * A boolean, decided by the segment's **midpoint**: inside the body means dashed, outside means
 * solid. Two things were wrong with it and only one is about convexity.
 *
 * A segment running half behind a block was drawn entirely one way, and which way depended on
 * where its middle happened to fall — so `AM` from a corner to a point on the far face flipped
 * between solid and dashed as the point was dragged past the halfway mark. And the halfspace
 * test itself assumes the body is convex, which every built-in one is and a body a teacher
 * builds need not be.
 *
 * Cutting answers both, and it is the same cutting the solid's own edges get: one rule for "what
 * does this body hide", rather than two that can disagree about the same figure.
 *
 * ## Why the mesh is taken into view space first
 *
 * `occludeSegment` needs its faces, its viewing direction and its projection to agree about
 * which space they are in. Converting once here is cheaper than per call and removes the
 * question — a solid drawn with a rotation would otherwise have its faces in one space and its
 * viewer direction in another, and the figure would dash convincingly wrong things.
 */
function occlusionPieces(mesh: Mesh): (a: Vec3, b: Vec3) => readonly Piece[] {
  const faces = mesh.faces.map((ring) =>
    ring.flatMap((index) => {
      const vertex = mesh.vertices[index];
      return vertex === undefined ? [] : [VIEW.toView(vertex)];
    })
  );

  const toViewer = viewerDirection(VIEW);
  const whole: readonly Piece[] = [{ from: 0, to: 1, hidden: false }];
  if (toViewer === null) return () => whole;

  const view = { project: (point: Vec3) => VIEW.toPlane(point), toViewer };
  return (a, b) => occludeSegment(VIEW.toView(a), VIEW.toView(b), faces, view);
}

/**
 * Every undirected face boundary, with the faces that meet along it.
 *
 * `mesh.ts` has its own version of this, private to it and coupled to the smooth-edge flag
 * its curved primitives need. Everything built here is flat, so every edge is hard and the
 * ten lines below are cheaper than exporting a shared helper with a parameter that would only
 * ever be passed one way.
 */
function edgesOf(faces: readonly (readonly number[])[]): readonly Edge[] {
  const byKey = new Map<string, { a: number; b: number; faces: number[] }>();

  faces.forEach((ring, face) => {
    for (let index = 0; index < ring.length; index += 1) {
      const a = ring[index];
      const b = ring[(index + 1) % ring.length];
      if (a === undefined || b === undefined || a === b) continue;
      const key = a < b ? `${String(a)}:${String(b)}` : `${String(b)}:${String(a)}`;
      const found = byKey.get(key);
      if (found === undefined) byKey.set(key, { a, b, faces: [face] });
      else found.faces.push(face);
    }
  });

  return [...byKey.values()].map((edge) => ({ ...edge, hard: true }));
}

/* --------------------------------------------------------------------- round solids */

/**
 * How finely a rim is sampled.
 *
 * The rim is a true ellipse and is drawn as a polyline, which needs saying plainly: the export
 * carries 32 points per half rather than one curve. It is not the ideal — an elliptical arc
 * value would be — but it is honest about what it is, and the alternative on offer was a *whole*
 * ellipse drawn solid, which would say the back of the base is visible when the solid covers it.
 *
 * The named points are unaffected. `rim O 210` is computed in space and projected exactly; the
 * polyline is only what the reader sees, and at this density the facets are below the width of
 * the stroke.
 */
const RIM_SEGMENTS = 64;

/** A cone: an apex over the centre of a circular base. */
export function namedCone(apex: string, centre: string, placement: SolidPlacement): NamedSolid {
  return round(
    { kind: SolidKind.Cone, taper: 1 },
    placement,
    [{ name: apex, y: 1 }],
    [{ name: centre, y: -1 }]
  );
}

/**
 * A sphere, named by its centre.
 *
 * ## Why this does not go through `round`
 *
 * Because a sphere's outline is not an edge of any mesh. The cone and the cylinder are drawn
 * from their tessellation: the silhouette runs along mesh edges where a front face meets a back
 * one, and for those two shapes it lands exactly on the true outline because the true outline
 * *is* a pair of straight generators and a rim. A sphere has no such edge anywhere on it, so the
 * same code returns the zigzag that separates the front band of quads from the back band —
 * rendered, it came out as a visibly faceted double outline with the latitude rings showing
 * through. A ball drawn as a sixteen-sided polygon is not a ball.
 *
 * So the two curves are emitted in closed form: the outline, and the equator that is what makes
 * a circle on the page read as a sphere at all.
 *
 * ## No rim
 *
 * A cone's `rim O 210` is an angle around a circular *face*, and a sphere has none. Every great
 * circle is as good as every other, so the number would silently mean whichever one this code
 * happened to pick. `rings` is left empty in the parser, which is what makes `rim O 90` refuse.
 *
 * ## Why a sphere hides nothing
 *
 * The other solids answer this with `occlusionTest`, and wiring the sphere up the same way is
 * the obvious move. It is the wrong one, for a reason about what the shape *is* rather than
 * about any drawing: a sphere in these problems is almost always circumscribed about a solid —
 * "mặt cầu ngoại tiếp hình chóp", eighteen problems in the book — and there it is a **locus**,
 * the points a distance R from O, not a block of stone. Every textbook draws the inner solid
 * exactly as it would be drawn alone and the sphere as an outline around it. A locus cannot be
 * in front of anything, so anything inside a sphere would have been dashed for no reason.
 *
 * Recorded honestly: no figure in the present language *shows* the difference, and one was
 * looked for. Only points that carry a position in space can be occluded at all, those come
 * from a solid, and a chord between two corners of a convex solid is already inside that solid
 * — so the cube's own diagonal comes out dashed either way, by the cube. `foot` could reach it.
 * The test is switched off because it states the wrong thing, not because a picture caught it.
 */
export function namedSphere(
  centre: string,
  placement: SolidPlacement,
  sections: readonly { readonly at: number; readonly tilt?: number }[] = []
): NamedSolid {
  const radius = placement.width / 2;
  const origin = placement.at;
  const middle = vec3(0, 0, 0);
  const toPage = (point: Vec3): Vec2 => onPage(point, origin);

  /*
   * The equator, split where it goes round the back.
   *
   * A point of it lies on the sphere, so the outward normal there is the point itself, and the
   * same test that decides whether a *face* points at the reader decides whether this piece of
   * the curve is in front of the body or behind it. Reusing that test rather than comparing `z`
   * is the difference between a split that is right for cabinet and one that is right only for
   * an orthographic view — the same mistake `project.ts` records having made once already.
   */
  const equator = Array.from({ length: RIM_SEGMENTS + 1 }, (_, index) => {
    const radians = (index / RIM_SEGMENTS) * 2 * Math.PI;
    return vec3(-radius * Math.sin(radians), 0, radius * Math.cos(radians));
  });
  const front: OutlineRun[] = [];
  const back: OutlineRun[] = [];
  let run: Vec2[] = [];
  let running: boolean | null = null;
  for (const point of equator) {
    const view = VIEW.toView(point);
    const near = VIEW.isFrontFacing(view, view);
    if (running !== null && near !== running) {
      (running ? front : back).push({ points: run, hidden: !running, covered: true });
      run = [run[run.length - 1] ?? toPage(point)];
    }
    running = near;
    run.push(toPage(point));
  }
  if (running !== null && run.length > 1) {
    (running ? front : back).push({ points: run, hidden: !running, covered: true });
  }

  return {
    /*
     * The ball's centre, then the centre of each cutting plane's circle.
     *
     * "Gọi H là hình chiếu của O lên (P)" opens nearly every question of this kind, and the
     * whole calculation is the right triangle OHA: `OH = d`, `HA = r`, `OA = R`. Without H
     * there is nothing to draw the distance to, nothing to mark the right angle at, and the
     * figure shows the circle while hiding the reason it has the radius it has.
     *
     * The foot of that perpendicular *is* the circle's centre, which is why one name does for
     * both — `circle H 60` reads as "the circle centred at H", exactly as a cone's `rim O 210`
     * takes the centre of its base. The order matters: the script's names are matched to these
     * by position.
     */
    vertices: [
      { name: centre, at: toPage(middle), in3d: middle },
      ...sections.map((section, index) => {
        // The circle's own middle, which is the foot of the perpendicular from O — and once the
        // plane leans, that is no longer straight up the axis.
        const at = sphereSectionBasis(section.at, section.tilt ?? 0).middle;
        return { name: `${centre}section${String(index)}`, at: toPage(at), in3d: at };
      }),
    ],
    edges: [],
    // Every plane cuts a ball in a circle, so there is nothing to say beyond its radius.
    round: { ball: true, rx: radius, ry: radius, rz: radius, taper: 1 },
    circles: [{ name: `${centre}equator`, ...projectedCircle(radius, 0, origin) }],
    /*
     * The same two curves as the polylines below, and these are the ones that reach the page.
     * Only the equator: a named sphere is nearly always circumscribed about another solid, and
     * a meridian drawn across that is clutter rather than help.
     */
    arcs: [
      ...(sphereConics(vec3(radius, radius, radius), VIEW, origin, 'equator') ?? []),
      /*
       * The circles a problem's planes cut out, each split at the silhouette exactly as the
       * equator is. A plane that misses the ball contributes nothing rather than a circle of
       * radius zero — the parser refuses that case, and this is the second lock on it.
       */
      ...sections.flatMap(
        (section) => sphereCircle(radius, section.at, VIEW, origin, section.tilt ?? 0) ?? []
      ),
    ],
    outline: [
      { points: ballOutline(radius, origin), hidden: false, covered: true },
      ...front,
      ...back,
    ],
    // Nothing. See above: a circumscribed sphere is a locus, and a locus is not in the way.
    occlude: () => [{ from: 0, to: 1, hidden: false }],
    project: toPage,
  };
}

/**
 * The outline of a ball, as a closed polyline.
 *
 * A parallel projection is an affine map, so the image of a ball is a filled ellipse and its
 * boundary is the ellipse whose shape matrix is `P Pᵀ` — the sum of the outer products of the
 * three axis images. That is `projectedCircle`'s calculation with a third basis vector added,
 * and the third one is why a sphere is not simply drawn as a circle: under cabinet the two
 * semi-axes come out about 7% apart, which is small and is not nothing.
 *
 * Returned as points rather than as an ellipse because the outline is what gets *drawn*, and
 * the drawn thing has to be able to be half-hidden like every other outline run. This one never
 * is — a silhouette is the boundary between what faces the reader and what does not, so no part
 * of it is behind anything — but the type is shared and the shape of the answer should not
 * depend on which solid happened to be asked.
 */
function ballOutline(radius: number, origin: Vec2): readonly Vec2[] {
  const image = (point: Vec3): Vec2 => VIEW.toPlane(VIEW.toView(point));
  const axes = [image(vec3(radius, 0, 0)), image(vec3(0, radius, 0)), image(vec3(0, 0, radius))];

  let xx = 0;
  let xy = 0;
  let yy = 0;
  for (const axis of axes) {
    xx += axis.x * axis.x;
    xy += axis.x * axis.y;
    yy += axis.y * axis.y;
  }

  const spread = Math.hypot(xx - yy, 2 * xy);
  const rx = Math.sqrt(Math.max(0, (xx + yy + spread) / 2));
  const ry = Math.sqrt(Math.max(0, (xx + yy - spread) / 2));
  const tilt = 0.5 * Math.atan2(2 * xy, xx - yy);

  const cos = Math.cos(tilt);
  const sin = Math.sin(tilt);
  return Array.from({ length: RIM_SEGMENTS + 1 }, (_, index) => {
    const radians = (index / RIM_SEGMENTS) * 2 * Math.PI;
    const u = rx * Math.cos(radians);
    const v = ry * Math.sin(radians);
    return vec.vec2(origin.x + u * cos - v * sin, origin.y + u * sin + v * cos);
  });
}

/** A cylinder: two circular faces, named by their centres. */
export function namedCylinder(bottom: string, top: string, placement: SolidPlacement): NamedSolid {
  return round(
    { kind: SolidKind.Cylinder, taper: 1 },
    placement,
    [],
    [
      { name: bottom, y: -1 },
      { name: top, y: 1 },
    ]
  );
}

/**
 * Shared body of the two round solids.
 *
 * `apexes` are named points on the axis with no rim of their own; `centres` are the ones a rim
 * belongs to, which is what makes `rim O 210` and `rim O2 210` mean different circles on a
 * cylinder and the same one on a cone.
 */
function round(
  shape: { kind: SolidKind; taper: number },
  placement: SolidPlacement,
  apexes: readonly { name: string; y: number }[],
  centres: readonly { name: string; y: number }[]
): NamedSolid {
  const radius = placement.width / 2;
  const half = placement.height / 2;

  const mesh = buildMesh({
    kind: shape.kind,
    width: placement.width,
    height: placement.height,
    depth: placement.width, // A circle, not an ellipse: the base is round in space.
    sides: RIM_SEGMENTS,
    taper: shape.taper,
  });
  const lattice = projectWith(mesh, VIEW, placement.at);

  const onAxis = (y: number): Vec3 => vec3(0, y * half, 0);
  const toPage = (point: Vec3): Vec2 => onPage(point, placement.at);

  const named = [...apexes, ...centres].map((entry) => ({
    name: entry.name,
    at: toPage(onAxis(entry.y)),
    in3d: onAxis(entry.y),
  }));

  /*
   * A rim point, from an angle in the base plane. Zero is the front — nearest the reader — and
   * the angle grows anticlockwise seen from above, so 90° is the left of the figure and 180°
   * the back. Matching the mesh's own winding is not required: the rim is a full circle, so
   * every angle lands on it whichever way round it was tessellated.
   */
  const rimAt = (centre: string, degrees: number): NamedVertex | null => {
    const found = centres.find((entry) => entry.name === centre);
    if (found === undefined) return null;
    const radians = (degrees * Math.PI) / 180;
    const in3d = vec3(-radius * Math.sin(radians), found.y * half, radius * Math.cos(radians));
    return { name: '', at: toPage(in3d), in3d };
  };

  /*
   * The rims, as the circles they are.
   *
   * A cone's base is a circle in space, and this projection is affine, so its image is exactly
   * an ellipse. Drawn from the lattice it was sixty-four chords instead — smooth enough at a
   * glance and a visibly kinked chain to anyone who zooms in, which is what the report was
   * about. The chords stay in `outline` to be measured and are marked as covered.
   */
  const rims = rimConics(mesh, lattice, VIEW, placement.at);
  const covered = new Set(rims.replaces);
  const onRim = (a: number, b: number): boolean => covered.has(edgeKey(a, b));

  return {
    vertices: named,
    edges: [],
    round: {
      ball: false,
      rx: radius,
      ry: half,
      rz: radius,
      taper: shape.kind === SolidKind.Cone ? 0 : shape.taper,
    },
    circles: centres.map((entry) => ({
      name: `${entry.name}rim`,
      ...projectedCircle(radius, entry.y * half, placement.at),
    })),
    arcs: rims.curves,
    outline: [
      // Split so that a run is wholly covered or wholly not: the two silhouette generators are
      // drawn as the straight lines they are, and the rims as the conics they are.
      ...chains(lattice, false, (a, b) => !onRim(a, b)).map((points) => ({
        points,
        hidden: false,
      })),
      ...chains(lattice, true, (a, b) => !onRim(a, b)).map((points) => ({
        points,
        hidden: true,
      })),
      ...chains(lattice, false, onRim).map((points) => ({
        points,
        hidden: false,
        covered: true,
      })),
      ...chains(lattice, true, onRim).map((points) => ({ points, hidden: true, covered: true })),
    ],
    occlude: occlusionPieces(mesh),
    project: toPage,
    rimAt,
  };
}

/**
 * The drawn segments of one visibility, joined end to end into runs.
 *
 * Emitted as runs rather than as sixty-four separate objects, because the object list is
 * something a teacher reads. "Rim, front half" is one thing in the figure and should be one
 * thing in the panel; sixty-four two-point segments is a list nobody can use.
 */
function chains(
  lattice: Lattice,
  hidden: boolean,
  include: (a: number, b: number) => boolean = () => true
): readonly (readonly Vec2[])[] {
  const remaining: [number, number][] = [];
  for (const edge of lattice.edges) {
    // A tessellation artefact is a line on the page only where it is a silhouette — that is
    // what turns sixty-four radial lines into the two sides a cone is drawn with.
    if (!edge.hard && !edge.silhouette) continue;
    if (edge.hard && edge.hidden !== hidden) continue;
    if (!edge.hard && hidden) continue;
    if (!include(edge.a, edge.b)) continue;
    remaining.push([edge.a, edge.b]);
  }

  const runs: (readonly Vec2[])[] = [];
  while (remaining.length > 0) {
    const seed = remaining.pop();
    if (seed === undefined) break;
    const indices = [seed[0], seed[1]];

    // Grow from both ends until nothing else touches them.
    for (let grew = true; grew;) {
      grew = false;
      for (let index = remaining.length - 1; index >= 0; index -= 1) {
        const edge = remaining[index];
        if (edge === undefined) continue;
        const [a, b] = edge;
        const first = indices[0];
        const last = indices[indices.length - 1];
        if (a === last) indices.push(b);
        else if (b === last) indices.push(a);
        else if (a === first) indices.unshift(b);
        else if (b === first) indices.unshift(a);
        else continue;
        remaining.splice(index, 1);
        grew = true;
      }
    }

    const points = indices.flatMap((index) => {
      const point = lattice.points[index];
      return point === undefined ? [] : [point];
    });
    if (points.length >= 2) runs.push(points);
  }

  return runs;
}

/**
 * A horizontal circle, as the ellipse it projects to.
 *
 * The projection maps the circle's two perpendicular radii onto two *conjugate* diameters,
 * which are in general neither perpendicular nor equal — so the answer is not "an ellipse with
 * these two radii" and cannot be read off the shear. The closed form below is the standard one
 * for the image of a unit circle under a 2x2 map: the semi-axes are its singular values and the
 * tilt comes from the same quadratic.
 *
 * Worth being exact about rather than approximating, because this ellipse is what the language
 * lets a problem attach to. A rim point computed one way and a rim ellipse computed another is
 * a differential test the corpus actually runs — `onEllipse` on a `rim` point passes only if
 * both agree.
 */
function projectedCircle(
  radius: number,
  height: number,
  origin: Vec2
): { center: Vec2; rx: number; ry: number; rotation: number } {
  // Images of the circle's own two radii: (r, 0, 0) and (0, 0, r).
  const along = VIEW.toPlane(VIEW.toView(vec3(radius, 0, 0)));
  const into = VIEW.toPlane(VIEW.toView(vec3(0, 0, radius)));

  const a = along.x;
  const c = along.y;
  const b = into.x;
  const d = into.y;

  const sum = a * a + b * b + c * c + d * d;
  const difference = a * a + b * b - c * c - d * d;
  const cross = 2 * (a * c + b * d);
  const spread = Math.hypot(difference, cross);

  const centre = VIEW.toPlane(VIEW.toView(vec3(0, height, 0)));

  return {
    center: vec.vec2(centre.x + origin.x, centre.y + origin.y),
    rx: Math.sqrt(Math.max(0, (sum + spread) / 2)),
    ry: Math.sqrt(Math.max(0, (sum - spread) / 2)),
    rotation: 0.5 * Math.atan2(cross, difference),
  };
}

/* ------------------------------------------------------- one entry for the evaluator */

/**
 * What a `solid-figure` needs: the drawn wireframe and the named corners, on the page and in
 * space.
 *
 * One shape for all four solids, because the evaluator should not have to know which of them it
 * is holding — that choice was made when the script was written, and re-making it at every
 * evaluation is how the four slowly grow four slightly different behaviours.
 */
export interface SolidSnapshot {
  readonly visible: readonly Segment[];
  readonly hidden: readonly Segment[];
  /**
   * The round parts as real conics, when the solid has any.
   *
   * `visible` and `hidden` above are the complete figure as a polyline, which is what bounds,
   * hit-testing and the spatial index measure. These two are the same figure for whoever is
   * going to *draw* it — the conics, and in `straight` only the parts no conic covers.
   */
  readonly curves?: readonly SolidCurve[];
  readonly straight?: {
    readonly visible: readonly Segment[];
    readonly hidden: readonly Segment[];
  };
  readonly points: readonly Vec2[];
  readonly in3d: readonly Vec3[];
  /**
   * The body and the projection, for the constructions that work in space.
   *
   * What a **plane** needs to cut a section and to put it back on the page. Absent for the round
   * solids, whose lateral surface is not a flat face: a plane through a cone meets it in a conic,
   * and a polygon would be the wrong answer drawn convincingly.
   */
  readonly faces?: readonly (readonly number[])[];
  readonly frame?: SolidFrame;
  /** The radii and taper of a round solid, with its projection. See {@link NamedSolid.round}. */
  readonly round?: { readonly body: RoundBody; readonly frame: SolidFrame };
  /**
   * Whether the body covers a line between two points in space.
   *
   * Carried out with the rest so that whoever draws the figure can ask about the lines the
   * *problem* added, not only about the solid's own edges. Those are as liable to run through
   * the middle — the diagonal of a cube, the common perpendicular between two skew edges — and
   * drawn solid they claim to lie on the near face.
   *
   * Stretches rather than a flag: a segment running half behind the body is drawn half dashed.
   * See {@link NamedSolid.occlude} for what the flag got wrong.
   */
  readonly occlude: (a: Vec3, b: Vec3) => readonly Piece[];
  /** Where this solid puts a point of its own space on the page. See the note where it is set. */
  readonly project: (at: Vec3) => Vec2;
  /**
   * The named edges, as pairs of indices into {@link points}.
   *
   * Carried because `visible` and `hidden` cannot answer *which* edge a line is. Their split is
   * a fact about the current rotation — turn the solid and an edge crosses from one array to the
   * other — so a position in either is no way to refer to an edge that survives the turn. A pair
   * of corner indices does: `SA` is `SA` from every angle.
   *
   * Only the straight, named edges. A cone's rim is a curve rather than a chain between corners,
   * and there is no pair of names that would describe a point on it.
   */
  readonly edges: readonly (readonly [number, number])[];
}

/** Builds the snapshot, or `null` for a description no solid answers to. */
export function buildNamedSolid(
  kind: 'prism' | 'pyramid' | 'cone' | 'cylinder' | 'sphere',
  placement: SolidPlacement,
  corners: number,
  foot: readonly number[],
  corner?: BaseCorner,
  explicit?: readonly { x: number; z: number }[],
  /** The planes cutting a sphere. Ignored by every other solid. */
  sections?: readonly { readonly at: number; readonly tilt?: number }[]
): SolidSnapshot | null {
  if (placement.width <= 0 || placement.height <= 0 || placement.depth <= 0) return null;

  const names = (count: number, prefix: string): string[] =>
    Array.from({ length: count }, (_, index) => `${prefix}${String(index)}`);

  const solid =
    kind === 'prism'
      ? corners < 3
        ? null
        : namedPrism(names(corners, 'a'), names(corners, 'b'), placement, foot, corner, explicit)
      : kind === 'pyramid'
        ? corners < 3
          ? null
          : namedPyramid('apex', names(corners, 'a'), placement, foot, corner, explicit)
        : kind === 'cone'
          ? namedCone('apex', 'centre', placement)
          : kind === 'sphere'
            ? namedSphere('centre', placement, sections)
            : namedCylinder('lower', 'upper', placement);

  if (solid === null) return null;

  const where = new Map(solid.vertices.map((vertex) => [vertex.name, vertex.at]));
  const index = new Map(solid.vertices.map((vertex, at) => [vertex.name, at]));
  const straight = solid.edges.flatMap((edge) => {
    const a = where.get(edge.a);
    const b = where.get(edge.b);
    return a === undefined || b === undefined
      ? []
      : [{ segment: { a, b }, hidden: edge.hidden, covered: false }];
  });

  const curved = (solid.outline ?? []).flatMap((run) =>
    run.points.slice(1).flatMap((point, index) => {
      const previous = run.points[index];
      return previous === undefined
        ? []
        : [
            {
              segment: { a: previous, b: point },
              hidden: run.hidden,
              covered: run.covered ?? false,
            },
          ];
    })
  );

  const all = [...straight, ...curved];
  const drawn = [...straight, ...curved.filter((edge) => !edge.covered)];
  const body = solid.body;
  const arcs = solid.arcs ?? [];

  return {
    occlude: solid.occlude,
    /*
     * Where this solid puts a point of its own space on the page.
     *
     * Carried so a caller can ask the question that matters when there is more than one solid:
     * *is this point mine?* Every solid's space is its own — two blocks at different places on
     * the page describe their corners with the very same numbers — so a point's coordinates
     * alone do not say which body they belong to. Run through the right solid's projection they
     * land where the point is drawn; through any other's they land somewhere else.
     */
    project: solid.project,
    // The complete polyline, for everything that measures rather than draws.
    visible: all.filter((edge) => !edge.hidden).map((edge) => edge.segment),
    hidden: all.filter((edge) => edge.hidden).map((edge) => edge.segment),
    ...(arcs.length === 0
      ? {}
      : {
          curves: arcs,
          // ...and the same figure for a painter: the conics, plus only the parts no conic
          // covers. See `OutlineRun.covered`.
          straight: {
            visible: drawn.filter((edge) => !edge.hidden).map((edge) => edge.segment),
            hidden: drawn.filter((edge) => edge.hidden).map((edge) => edge.segment),
          },
        }),
    points: solid.vertices.map((vertex) => vertex.at),
    in3d: solid.vertices.map((vertex) => vertex.in3d),
    ...(body === undefined ? {} : { faces: body.faces, frame: frameOfView(placement.at) }),
    ...(solid.round === undefined
      ? {}
      : { round: { body: solid.round, frame: frameOfView(placement.at) } }),
    edges: solid.edges.flatMap((edge) => {
      const a = index.get(edge.a);
      const b = index.get(edge.b);
      return a === undefined || b === undefined ? [] : [[a, b] as const];
    }),
  };
}

/**
 * A point on a base circle of a round solid, `degrees` round from the front.
 *
 * Rebuilt from the extents rather than remembered, so it follows the solid when the solid
 * changes — which is the whole reason any of this moved out of the parser.
 */
export function rimPoint(
  origin: Vec2,
  extent: { readonly width: number; readonly height: number; readonly depth: number },
  ring: number,
  degrees: number,
  /**
   * A sphere's cutting planes, when this is one. `ring` then indexes them rather than choosing
   * between a lower rim and an upper one.
   *
   * The two cases are one function because everything after the first two lines is the same —
   * an angle round a horizontal circle — and the difference is only which circle. Splitting them
   * would put that shared arithmetic in two places, which is how the two come to disagree about
   * where 0° points, and a figure whose `rim 0` and `on 0` face opposite ways is worse than
   * either convention.
   */
  sections?: readonly { readonly at: number; readonly tilt?: number }[]
): { readonly at: Vec2; readonly in3d: Vec3 } | null {
  if (extent.width <= 0 || extent.height <= 0) return null;

  const ball = extent.width / 2;
  const section = sections?.[ring];
  const height = section?.at;

  /*
   * On a sphere the ring's own radius follows from how far its plane is from the centre —
   * `r² + d² = R²`, the theorem the question is about — so it is worked out here rather than
   * stored. On a cylinder or a cone the ring *is* the body's own circle, at half its height.
   */
  const radius = height === undefined ? ball : Math.sqrt(ball * ball - height * height);
  if (!(radius > 0)) return null;

  const radians = (degrees * Math.PI) / 180;

  /*
   * A sphere's ring is walked on the **same basis the drawing uses**, so a point at 0° is where
   * the drawn circle's 0° is. Once the plane leans, a point worked out as `(x, height, z)` is no
   * longer even on the ring — and it would look almost right, which is the worst amount of wrong.
   */
  if (section !== undefined) {
    const basis = sphereSectionBasis(section.at, section.tilt ?? 0);
    const cos = Math.cos(radians);
    const sin = Math.sin(radians);
    const in3d = vec3(
      basis.middle.x + radius * (basis.u.x * cos + basis.v.x * sin),
      basis.middle.y + radius * (basis.u.y * cos + basis.v.y * sin),
      basis.middle.z + radius * (basis.u.z * cos + basis.v.z * sin)
    );
    return { at: onPage(in3d, origin), in3d };
  }

  const at = ring === 0 ? -extent.height / 2 : extent.height / 2;
  const in3d = vec3(-radius * Math.sin(radians), at, radius * Math.cos(radians));
  return { at: onPage(in3d, origin), in3d };
}

/** A point in space, on the page, under the figure projection. */
export function projectOnto(origin: Vec2, at: Vec3): Vec2 {
  return onPage(at, origin);
}

/**
 * The foot of the perpendicular from `from` to the line through `a` and `b`, in space.
 *
 * `null` when the two points coincide: there is no line, and a degenerate construction has no
 * value — the same answer the rest of the kernel gives.
 */
export function perpendicularFoot(from: Vec3, a: Vec3, b: Vec3): Vec3 | null {
  const along = vec3(b.x - a.x, b.y - a.y, b.z - a.z);
  const lengthSquared = along.x * along.x + along.y * along.y + along.z * along.z;
  if (lengthSquared === 0) return null;

  const offset = vec3(from.x - a.x, from.y - a.y, from.z - a.z);
  const t = (offset.x * along.x + offset.y * along.y + offset.z * along.z) / lengthSquared;
  return vec3(a.x + along.x * t, a.y + along.y * t, a.z + along.z * t);
}

/**
 * A stored base, back as points.
 *
 * The document holds the ring flat, so every reader has to pair it up again, and doing that in
 * two places is how two readers come to disagree about which number is x.
 */
export function ringOf(flat: readonly number[]): readonly { x: number; z: number }[] {
  return Array.from({ length: Math.floor(flat.length / 2) }, (_, k) => ({
    x: flat[k * 2] ?? 0,
    z: flat[k * 2 + 1] ?? 0,
  }));
}

/**
 * What to call corner `index` of a solid standing on a base of `corners` sides.
 *
 * `A`…`Z` round the floor, then the same letters primed for the top ring, and `S` for a pyramid's
 * apex — the lettering every Vietnamese textbook uses and the one the `prism` and `pyramid`
 * statements already produce. Past twenty-six corners the letters run out and the numbering takes
 * over, which is a base nobody will draw and a name nobody will read.
 *
 * It lives here rather than beside one of its callers because three paths now produce a solid —
 * the script, a solid raised on a drawn base, and a solid dropped from the toolbar — and a figure
 * lettered differently depending on which one built it would teach a teacher that the lettering
 * means something. `buildMesh` numbers every one of them the same way: the base ring first, then
 * the top ring in matching order, with a pyramid's apex last.
 */
export function cornerName(index: number, corners: number, kind: 'prism' | 'pyramid'): string {
  if (kind === 'pyramid' && index >= corners) return 'S';

  const letter = String.fromCharCode(65 + (index % corners));
  if (index % corners !== index && index >= 26) return String(index + 1);
  return index < corners ? letter : `${letter}'`;
}
